<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :status="pageStatus"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button
          v-if="!labourer.biotime_id"
          class="md-info md-round"
          @click="biotimeRegistration(false)"
      >
        <md-icon>fingerprint</md-icon>
        Biotime Reg
      </md-button>

      <md-button
          v-if="labourer.biotime_id"
          class="md-rose md-round"
          @click="goToBiotimeSync"
      >
        <md-icon>sync</md-icon>
        Retrieve Punch
      </md-button>

      <md-button
          v-if="labourer.biotime_id && can('create-biotime-attendance')"
          class="md-rose md-round"
          @click="biotimeRegistration(true)"
      >
        <md-icon>add</md-icon>
        Force Biometric
      </md-button>

      <md-button class="md-primary md-round" @click="goToEdit(labourer.id)">
        <md-icon>edit</md-icon>
        Edit
      </md-button>

      <md-button class="md-info md-round" @click="goBack">
        <md-icon>arrow_back</md-icon>
        Back
      </md-button>
    </template>

    <template #content>
      <div class="labourer-profile">
        <labourer-card :labourer="labourer" @image-updated="refreshLabourer" />
      </div>

      <page-tabs>
        <!-- Skills Tab -->
        <md-tab id="tab-skills" md-label="Skills" md-icon="psychology">
          <div class="tab-content">
            <table-data
                title="Skills"
                :columns="skillColumns"
                :data="labourer.skills"
                :total="labourer.skills.length"
                :searchable="true"
                search-placeholder="Search skills..."
                storage-key="labourer-skills"
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="showSkillModal">
                  <md-icon>add</md-icon>
                  Assign Skill
                </md-button>
              </template>

              <template #cell-name="{ item }">
                <chip type="primary" icon="psychology">{{ item.name }}</chip>
              </template>

              <template #actions="{ item }">
                <md-button class="md-icon-button md-danger" @click="deleteSkillRow(item)" title="Remove">
                  <md-icon>delete</md-icon>
                </md-button>
              </template>

              <template #empty-actions>
                <md-button class="md-primary md-round" @click="showSkillModal">
                  <md-icon>add</md-icon>
                  Assign First Skill
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Vendors Tab -->
        <md-tab id="tab-vendor" md-label="Vendors" md-icon="store">
          <div class="tab-content">
            <table-data
                title="Vendors"
                :columns="vendorColumns"
                :data="labourer.labourerVendors"
                :total="labourer.labourerVendors.length"
                :searchable="false"
                storage-key="labourer-vendors"
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="showVendorModal">
                  <md-icon>add</md-icon>
                  Assign Vendor
                </md-button>
              </template>

              <template #cell-vendor_name="{ item }">
                {{ item.vendor ? item.vendor.name : 'N/A' }}
              </template>

              <template #actions="{ item }">
                <md-button class="md-icon-button md-danger" @click="deleteVendorRow(item)" title="Remove">
                  <md-icon>delete</md-icon>
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Relatives Tab -->
        <md-tab id="tab-parent" md-label="Relatives" md-icon="people">
          <div class="tab-content">
            <table-data
                title="Relatives"
                :columns="parentColumns"
                :data="labourer.parents"
                :total="labourer.parents.length"
                :searchable="true"
                search-placeholder="Search relatives..."
                storage-key="labourer-relatives"
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="showParentModal">
                  <md-icon>add</md-icon>
                  Add Relative
                </md-button>
              </template>

              <template #actions="{ item }">
                <md-button class="md-icon-button md-danger" @click="deleteParentRow(item)" title="Remove">
                  <md-icon>delete</md-icon>
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Education Tab -->
        <md-tab id="tab-education" md-label="Education" md-icon="school">
          <div class="tab-content">
            <table-data
                title="Education History"
                :columns="educationColumns"
                :data="labourer.educations"
                :total="labourer.educations.length"
                :searchable="true"
                search-placeholder="Search education..."
                storage-key="labourer-education"
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="showEducationModal">
                  <md-icon>add</md-icon>
                  Add Education
                </md-button>
              </template>

              <template #actions="{ item }">
                <md-button class="md-icon-button md-danger" @click="deleteEducationRow(item)" title="Remove">
                  <md-icon>delete</md-icon>
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Biotime Areas Tab (NEW) -->
        <md-tab id="tab-biotime-areas" md-label="Biotime Areas" md-icon="fingerprint">
          <div class="tab-content">
            <table-data
                title="Assigned Biotime Areas"
                :columns="biotimeAreaColumns"
                :data="labourer.biotimeAreas || []"
                :total="(labourer.biotimeAreas || []).length"
                :searchable="true"
                search-placeholder="Search areas..."
                storage-key="labourer-biotime-areas"
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="openBiotimeAreasModal">
                  <md-icon>add</md-icon>
                  Manage Areas
                </md-button>
              </template>

              <template #cell-area_name="{ item }">
                <strong>{{ item.area_name }}</strong>
              </template>

              <template #cell-area_code="{ item }">
                <code class="area-code-chip">{{ item.area_code }}</code>
              </template>

              <template #cell-parent_area="{ item }">
                <span v-if="item.parent_area">{{ item.parent_area }}</span>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell-station_name="{ item }">
                <chip v-if="item.station_name" type="info" icon="business" size="small">
                  {{ item.station_name }}
                </chip>
                <chip v-else type="warning" icon="help_outline" size="small">
                  Unassigned
                </chip>
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-danger"
                    @click="removeBiotimeArea(item)"
                    title="Remove"
                >
                  <md-icon>delete</md-icon>
                </md-button>
              </template>

              <template #empty-actions>
                <md-button class="md-primary md-round" @click="openBiotimeAreasModal">
                  <md-icon>add</md-icon>
                  Manage Biotime Areas
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- TakeOn Tab -->
        <md-tab id="tab-takeon" md-label="TakeOn" md-icon="note">
          <div class="tab-content">
            <table-data
                title="TakeOn Records"
                :columns="takeOnColumns"
                :data="labourer.labourerSchedules"
                :total="labourer.labourerSchedules.length"
                :searchable="true"
                search-placeholder="Search takeons..."
                storage-key="labourer-takeons"
                :has-actions="true"
            >
              <template #cell-status="{ item }">
                <chip :type="getTakeonStatusType(item.status)">
                  {{ item.status }}
                </chip>
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-primary"
                    @click="viewTakeon(item)"
                    title="View"
                >
                  <md-icon>visibility</md-icon>
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Priority Workplaces Tab -->
        <md-tab id="tab-favorite" md-label="Priority W/p" md-icon="favorite">
          <div class="tab-content">
            <table-data
                title="Priority Workplaces"
                :columns="activityWorkplaceColumns"
                :data="labourer.activityWorkplaces"
                :total="labourer.activityWorkplaces.length"
                :searchable="true"
                search-placeholder="Search workplaces..."
                storage-key="labourer-workplaces"
                :has-actions="true"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="showActivityWorkplaceModal">
                  <md-icon>add</md-icon>
                  Add Activity Workplace
                </md-button>
              </template>

              <template #cell-start_date="{ item }">
                {{formatDateTime(item.start_date)}}
              </template>

              <template #cell-end_date="{ item }">
                  {{formatDateTime(item.end_date)}}
              </template>

              <template #cell-status="{ item }">
                <chip :type="getActivityStatusType(item.status)">
                  {{ item.status }}
                </chip>
              </template>

              <template #actions="{ item }">
                <md-button
                    class="md-icon-button md-warning"
                    @click="endActivityWorkplace(item)"
                    title="End Assignment"
                >
                  <md-icon>event_busy</md-icon>
                </md-button>
                <md-button
                    v-if="can('delete-labourer-activity-workplace')"
                    class="md-icon-button md-danger"
                    @click="deletePermanentActivityWorkplace(item)"
                    title="Delete"
                >
                  <md-icon>delete</md-icon>
                </md-button>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Statuses Tab -->
        <md-tab id="tab-status" md-label="Statuses" md-icon="check_circle">
          <div class="tab-content">
            <table-data
                title="Status History"
                :columns="statusColumns"
                :data="labourer.statuses"
                :total="labourer.statuses.length"
                :searchable="true"
                search-placeholder="Search status history..."
                storage-key="labourer-statuses"
                :has-actions="false"
            >
              <template #toolbar-right>
                <md-button class="md-primary md-round" @click="showStatusModal">
                  <md-icon>add</md-icon>
                  Change Status
                </md-button>
              </template>

              <template #cell-name="{ item }">
                <chip :type="getStatusChipType(item.name)">
                  {{ item.name }}
                </chip>
              </template>
            </table-data>
          </div>
        </md-tab>

        <!-- Attachments Tab -->
        <md-tab id="tab-attachments" md-label="Attachments" md-icon="attach_file">
          <div class="tab-content">
            <div class="attachment-form">
              <form @submit.prevent="addAttachment">
                <div class="md-layout md-gutter">
                  <div class="md-layout-item md-size-50">
                    <select-input
                        v-model="attachment.attachment_type_id"
                        track-by="id"
                        label="Attachment Type"
                        placeholder="Select Type"
                        :options="typesOptions"
                        :allow-empty="false"
                        :error="apiValidationErrors.attachment_type_id"
                    />
                  </div>
                  <div class="md-layout-item md-size-50">
                    <div class="file-input-wrapper">
                      <md-button class="md-success" @click="triggerFileUpload">
                        <md-icon>cloud_upload</md-icon>
                        {{ image ? 'Change File' : 'Select File' }}
                      </md-button>
                      <input
                          type="file"
                          ref="fileInput"
                          @change="onFileChange"
                          style="display: none"
                          accept="image/*,application/pdf"
                      />
                      <span v-if="fileName" class="file-name">{{ fileName }}</span>
                    </div>
                    <validation-error :errors="apiValidationErrors.image" />
                  </div>
                </div>
                <div class="form-actions">
                  <md-button type="submit" class="md-primary md-raised" :disabled="isSubmitting">
                    <md-icon>attach_file</md-icon>
                    {{ isSubmitting ? 'Uploading...' : 'Add Attachment' }}
                  </md-button>
                </div>
              </form>
            </div>

            <md-table v-if="attachmentsLists && attachmentsLists.length">
              <md-table-row>
                <md-table-head>Preview</md-table-head>
                <md-table-head>File Name</md-table-head>
                <md-table-head>Attachment Type</md-table-head>
                <md-table-head>Actions</md-table-head>
              </md-table-row>
              <md-table-row v-for="attachData in attachmentsLists" :key="attachData.id">
                <md-table-cell>
                  <div class="attachment-preview">
                    <img
                        v-if="isImage(attachData.path)"
                        :src="default_path + attachData.path"
                        class="attachment-thumbnail"
                        @click="previewAttachment(attachData)"
                    />
                    <md-icon v-else class="file-icon">insert_drive_file</md-icon>
                  </div>
                </md-table-cell>
                <md-table-cell>
                  <a :href="default_path + attachData.path" target="_blank" class="attachment-link">
                    {{ getFileName(attachData.path) }}
                  </a>
                </md-table-cell>
                <md-table-cell>
                  {{ attachData.attachmentType ? attachData.attachmentType.name : 'N/A' }}
                </md-table-cell>
                <md-table-cell>
                  <md-button class="md-icon-button md-primary" @click="previewAttachment(attachData)" title="Preview">
                    <md-icon>visibility</md-icon>
                  </md-button>
                  <md-button class="md-icon-button md-danger" @click="destroyAttachment(attachData.id)" title="Delete">
                    <md-icon>delete</md-icon>
                  </md-button>
                </md-table-cell>
              </md-table-row>
            </md-table>
            <div v-else class="empty-state">
              <md-icon>attach_file</md-icon>
              <p>No attachments available</p>
            </div>
          </div>
        </md-tab>

        <!-- Issues Tab -->
        <md-tab id="tab-issues" md-label="Issues" md-icon="report_problem">
          <div class="tab-content">
            <div class="issues-section" v-if="localIssues && localIssues.length > 0">
              <div class="issues-list">
                <div v-for="issue in localIssues" :key="issue.id" class="issue-item">
                  <div class="issue-header">
                    <div class="issue-category">
                      <chip :type="issue.priority_badge">{{ issue.priority_label }}</chip>
                      <strong>{{ issue.category_label }}</strong>
                    </div>
                    <div class="issue-status">
                      <chip :type="issue.status_badge">{{ issue.status_label }}</chip>
                    </div>
                  </div>
                  <div class="issue-description">{{ issue.description }}</div>
                  <div class="issue-meta">
                    <span>Reported: {{ formatDate(issue.created_at) }}</span>
                    <span v-if="issue.resolved_at">Resolved: {{ formatDate(issue.resolved_at) }}</span>
                    <span v-if="issue.reported_by">By: {{ issue.reported_by ? issue.reported_by.name : 'Unknown' }}</span>
                  </div>
                  <div class="issue-actions" v-if="issue.status === 'open' || issue.status === 'in_progress'">
                    <md-button class="md-icon-button md-success" @click="resolveIssue(issue)" title="Resolve">
                      <md-icon>check_circle</md-icon>
                    </md-button>
                    <md-button class="md-icon-button md-info" @click="editIssue(issue)" title="Edit">
                      <md-icon>edit</md-icon>
                    </md-button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <md-icon>report_problem</md-icon>
              <p>No issues reported</p>
            </div>
          </div>
        </md-tab>
      </page-tabs>
      <add-parent-modal
          :title="'Add Parent to ' + labourer.labourer_number"
          :showModal="showParentForm"
          model="Labourer"
          :labourer="labourer"
          modelType="labourerParents/list"
          @closeModal="hideParentModal"
          @addParents="addParents"
      />
      <add-activity-workplace-modal
          :title="'Assign Workplace to Labourer ' + labourer.labourer_number"
          :showModal="showActivityWorkplaceForm"
          model="Labourer"
          :labourer="labourer"
          modelType="activityLabourerWorkplace/list"
          @closeModal="hideActivityWorkplaceModal"
          @addActivityLabourerWorkplaces="addActivityWorkplace"
      />
      <add-status-modal
          :title="'Change Status for ' + labourer.nida_number"
          :showModal="showStatusForm"
          model="Labourer"
          :labourer="labourer"
          :statusOptions="statusOptions"
          @closeModal="hideStatusModal"
          @addStatus="addStatus"
      />
      <add-education-modal
          :title="'Add Education to ' + labourer.labourer_number"
          :showModal="showEducationForm"
          model="Labourer"
          :labourer="labourer"
          modelType="labourerEducation/list"
          @closeModal="hideEducationModal"
          @addEducations="addEducations"
      />
      <add-skill-modal
          :title="'Add Skill to ' + labourer.labourer_number"
          :showModal="showSkillForm"
          model="Labourer"
          modelType="skills/list"
          @closeModal="hideSkillModal"
          @addSkills="addSkills"
      />

      <!-- Attachment Preview Dialog -->
      <md-dialog :md-active.sync="previewDialog.active">
        <md-dialog-title>Attachment Preview</md-dialog-title>
        <md-dialog-content>
          <div class="preview-container">
            <img v-if="previewDialog.isImage" :src="previewDialog.url" class="preview-image"/>
            <iframe v-else :src="previewDialog.url" class="preview-pdf"></iframe>
          </div>
        </md-dialog-content>
        <md-dialog-actions>
          <md-button :href="previewDialog.url" target="_blank" class="md-primary">
            <md-icon>open_in_new</md-icon>
            Open
          </md-button>
          <md-button class="md-danger" @click="previewDialog.active = false">Close</md-button>
        </md-dialog-actions>
      </md-dialog>

      <!-- Issue Modal -->
      <issue-modal
          v-model="showIssueModal"
          :labourer-id="labourerId"
          :issue="selectedIssue"
          :users="users"
          @success="onIssueSuccess"
      />

      <!-- Resolve Modal -->
      <md-dialog :md-active.sync="showResolveModal" class="resolve-modal">
        <md-dialog-title>Resolve Issue</md-dialog-title>
        <md-dialog-content>
          <p>Are you sure you want to resolve this issue?</p>
          <md-field>
            <label>Resolution Comments</label>
            <md-textarea v-model="resolveComments" rows="2"></md-textarea>
          </md-field>
        </md-dialog-content>
        <md-dialog-actions>
          <md-button class="md-primary" @click="confirmResolve" :disabled="isResolving">
            {{ isResolving ? 'Resolving...' : 'Resolve' }}
          </md-button>
          <md-button class="md-danger" @click="showResolveModal = false">Cancel</md-button>
        </md-dialog-actions>
      </md-dialog>
      <assign-biotime-area-modal
          v-model="showBiotimeAreasModal"
          :labourer-id="labourer.id"
          :labourer-name="getFullName()"
          :assigned-area-ids="assignedBiotimeAreaIds"
          @success="onBiotimeAreasSaved"
      />
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import PageTabs from '@/components/Layouts/PageTabs.vue';
import TableData from '@/components/DataTable/TableData.vue';
import Chip from '@/components/Chip.vue';
import LabourerCard from './LabourerCard.vue';
import SelectInput from '@/components/Inputs/SelectInput.vue';
import ValidationError from '@/components/ValidationError.vue';
import AssignBiotimeAreaModal from './components/AssignBiotimeAreaModal.vue';
import AddParentModal from '@/components/Modals/AddParentModal.vue';
import AddActivityWorkplaceModal from './components/AddActivityWorkplaceModal.vue';
import AddStatusModal from './components/AddStatusModal.vue';
import AddEducationModal from '@/components/Modals/AddEducationModal.vue';
import AddSkillModal from '@/components/Modals/AddSkillModal.vue';
import PageMixin from '@/mixins/page-mixin';
import formMixin from '@/mixins/form-mixin';
import dateFormatMixin from '@/mixins/date-format-mixin';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

export default {
  name: 'ViewLabourerPage',
  components: {
    PageLayout: PageLayout,
    PageTabs: PageTabs,
    TableData: TableData,
    Chip: Chip,
    LabourerCard: LabourerCard,
    SelectInput: SelectInput,
    ValidationError: ValidationError,
    AssignBiotimeAreaModal: AssignBiotimeAreaModal,
    AddParentModal: AddParentModal,
    AddActivityWorkplaceModal: AddActivityWorkplaceModal,
    AddStatusModal: AddStatusModal,
    AddEducationModal: AddEducationModal,
    AddSkillModal: AddSkillModal
  },
  mixins: [PageMixin, formMixin, dateFormatMixin],
  data() {
    return {
      labourer: {
        id: null,
        first_name: null,
        middle_name: null,
        last_name: null,
        nida_number: null,
        phone_number: null,
        email: null,
        gender: null,
        birth_date: null,
        image: null,
        skilled: null,
        biotime_id: null,
        skills: [],
        educations: [],
        parents: [],
        labourerSchedules: [],
        labourerVendors: [],
        activityWorkplaces: [],
        biotimeAreas: [],
        statuses: [],
        port: null,
        department: null,
        vendor: null
      },
      statusOptions: [],
      // Column definitions
      skillColumns: [
        { field: 'name', label: 'Skill' }
      ],
      vendorColumns: [
        { field: 'vendor_name', label: 'Vendor' },
        { field: 'vendor_labourer_number', label: 'Vendor Ref.' },
        { field: 'labourer_start_date', label: 'Start' },
        { field: 'labourer_end_date', label: 'End' }
      ],
      parentColumns: [
        { field: 'name', label: 'Name' },
        { field: 'parent_nature', label: 'Relationship' },
        { field: 'phone_number', label: 'Phone' },
        { field: 'address', label: 'Address' }
      ],
      educationColumns: [
        { field: 'school_name', label: 'School' },
        { field: 'exam_year', label: 'Year' },
        { field: 'index_number', label: 'Index #' }
      ],
      biotimeAreaColumns: [
        { field: 'area_name', label: 'Area Name' },
        { field: 'area_code', label: 'Code' },
        { field: 'parent_area', label: 'Parent Area' },
        { field: 'station_name', label: 'Station' }
      ],
      takeOnColumns: [
        { field: 'takeon_number', label: 'Takeon #' },
        { field: 'schedule.shift_date', label: 'Shift Date' },
        { field: 'activity.name', label: 'Activity' },
        { field: 'workplace.name', label: 'Workplace' },
        { field: 'paid_amount', label: 'Amount', align: 'text-right' },
        { field: 'status', label: 'Status' }
      ],
      activityWorkplaceColumns: [
        { field: 'activity.name', label: 'Activity' },
        { field: 'workplace.name', label: 'Workplace' },
        { field: 'start_date', label: 'Start Date' },
        { field: 'end_date', label: 'End Date' },
        { field: 'status', label: 'Status' }
      ],
      statusColumns: [
        { field: 'name', label: 'Status' },
        { field: 'reason', label: 'Reason' },
        { field: 'created_at', label: 'Date' }
      ],
      // Modals
      showSkillForm: false,
      showParentForm: false,
      showActivityWorkplaceForm: false,
      showStatusForm: false,
      showEducationForm: false,
      showBiotimeAreasModal: false,
      // Attachments
      attachmentsLists: [],
      typesOptions: [],
      attachment: {
        type: 'attachments',
        path: null,
        description: 'no description',
        attachment_type: 'Labourer',
        attachment_id: null,
        attachment_type_id: null
      },
      image: null,
      fileName: null,
      file: null,
      isSubmitting: false,
      default_path: process.env.VUE_APP_BASE_URL,
      previewDialog: {
        active: false,
        url: null,
        isImage: true
      },
      // Issues
      showIssueModal: false,
      showResolveModal: false,
      selectedIssue: null,
      resolveComments: '',
      isResolving: false,
      resolveIssueId: null,
      localIssues: [],
      localStats: null
    };
  },
  computed: {
    pageTitle: function () {
      return this.getFullName() !== 'N/A' ? this.getFullName() : 'Labourer Details';
    },
    pageBreadcrumb: function () {
      return [
        { label: 'Labourers', path: '/labourers' },
        { label: this.getFullName() || 'Details' }
      ];
    },
    pageStatus: function () {
      var statusName = this.getCurrentStatusName();
      return {
        label: statusName,
        type: this.getStatusChipType(statusName)
      };
    },
    pageInfoCards: function () {
      return [
        {
          icon: 'assignment_ind',
          label: 'Total Takeons',
          value: this.labourer.labourerSchedules ? this.labourer.labourerSchedules.length : 0,
          color: 'primary'
        },
        {
          icon: 'psychology',
          label: 'Skills',
          value: this.labourer.skills ? this.labourer.skills.length : 0,
          color: 'info'
        },
        {
          icon: 'fingerprint',
          label: 'Biotime Areas',
          value: this.labourer.biotimeAreas ? this.labourer.biotimeAreas.length : 0,
          color: 'success'
        },
        {
          icon: 'verified_user',
          label: 'Biotime Status',
          value: this.labourer.biotime_id ? 'Registered' : 'Unregistered',
          color: this.labourer.biotime_id ? 'success' : 'warning'
        }
      ];
    },
    labourerId: function () {
      return this.$route.params.id;
    },
    users: function () {
      return this.$store.getters['users/list'] || [];
    },
    /** IDs of assigned biotime areas (for the modal) */
    assignedBiotimeAreaIds: function () {
      if (!this.labourer.biotimeAreas) return [];
      return this.labourer.biotimeAreas.map(function (a) { return a.id; });
    }
  },
  created() {
    this.get();
    this.getAttachmentTypes();
    this.getAttachments();
    this.getStatusOptions();
    this.fetchLabourerIssues();
  },
  methods: {
    // ============ FETCH ============
    async get() {
      try {
        var params = {
          id: this.$route.params.id,
          include: [
            'labourerSchedules',
            'labourerSchedules.workplace',
            'labourerSchedules.schedule',
            'labourerSchedules.activity',
            'skills',
            'parents',
            'educations.exam',
            'activityWorkplaces.activity',
            'activityWorkplaces.workplace',
            'activityLabourerWorkplace.workplace',
            'activityLabourerWorkplace.activity.skills',
            'port',
            'department',
            'labourerVendors.vendor',
            'statuses',
            'statuses.creator',
            'biotimeAreas',           // ← NEW
            'biotimeAreas.station'    // ← NEW
          ].join(',')
        };
        await this.$store.dispatch('labourers/view', params);
        var loaded = this.$store.getters['labourers/labourer'];

        // Ensure arrays exist
        loaded.skills = loaded.skills || [];
        loaded.educations = loaded.educations || [];
        loaded.parents = loaded.parents || [];
        loaded.labourerSchedules = loaded.labourerSchedules || [];
        loaded.labourerVendors = loaded.labourerVendors || [];
        loaded.activityWorkplaces = loaded.activityWorkplaces || [];
        loaded.biotimeAreas = loaded.biotimeAreas || [];
        loaded.statuses = loaded.statuses || [];

        this.labourer = loaded;
      } catch (error) {
        console.error('Error fetching labourer:', error);
        this.notifyException(error);
      }
    },

    refreshLabourer() {
      this.get();
    },

    async update() {
      try {
        await this.$store.dispatch('labourers/update', this.labourer);
        this.notifySuccess('Labourer updated successfully');
        await this.get();
      } catch (error) {
        this.notifyException(error);
      }
    },

    // ============ HELPERS ============
    getFullName: function () {
      var parts = [];
      if (this.labourer.first_name) parts.push(this.labourer.first_name);
      if (this.labourer.middle_name) parts.push(this.labourer.middle_name);
      if (this.labourer.last_name) parts.push(this.labourer.last_name);
      return parts.length ? parts.join(' ') : 'N/A';
    },

    getCurrentStatusName: function () {
      if (this.labourer.statuses && this.labourer.statuses.length) {
        return this.labourer.statuses[this.labourer.statuses.length - 1].name || 'Unknown';
      }
      return 'No Status';
    },

    getStatusChipType: function (statusName) {
      var map = {
        'Active': 'success',
        'Suspended': 'warning',
        'Terminated': 'danger',
        'Inactive': 'secondary'
      };
      return map[statusName] || 'info';
    },

    getTakeonStatusType: function (status) {
      var map = {
        'Assigned': 'success',
        'Unsigned': 'danger',
        'pending': 'warning',
        'Rejected': 'warning',
        'Checked In': 'info',
        'Checked Out': 'success'
      };
      return map[status] || 'secondary';
    },

    getActivityStatusType: function (status) {
      var map = {
        'Assigned': 'success',
        'Unsigned': 'danger',
        'pending': 'warning',
        'Rejected': 'warning'
      };
      return map[status] || 'secondary';
    },

    formatDate: function (date) {
      if (!date) return 'N/A';
      return dayjs(date).format('DD MMM YYYY');
    },
    formatDateTime: function (date) {
      if (!date) return 'N/A';
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    },

    // ============ BIOTIME AREAS (NEW) ============
    openBiotimeAreasModal() {
      this.showBiotimeAreasModal = true;
    },

    onBiotimeAreasSaved() {
      this.get(); // refresh labourer with new biotimeAreas
    },

    async removeBiotimeArea(area) {
      var self = this;
      var confirmation = await Swal.fire({
        title: 'Remove Area?',
        text: 'Remove "' + area.area_name + '" from this labourer?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      });

      if (!confirmation.value) return;

      var newIds = this.assignedBiotimeAreaIds.filter(function (id) {
        return id !== area.id;
      });

      try {
        await this.$store.dispatch('labourers/syncBiotimeAreas', {
          labourer_id: this.labourer.id,
          biotime_area_ids: newIds
        });
        this.notifySuccess('Area removed');
        await this.get();
      } catch (error) {
        this.notifyError('Failed to remove area');
      }
    },

    // ============ SKILLS ============
    showSkillModal() { this.showSkillForm = true; },
    hideSkillModal() { this.showSkillForm = false; },

    deleteSkillRow(item) {
      var idx = this.labourer.skills.findIndex(function (s) { return s.id === item.id; });
      if (idx >= 0) {
        this.labourer.skills.splice(idx, 1);
        this.update();
      }
    },

    async addSkills(skills) {
      var existingIds = this.labourer.skills.map(function (s) { return s.id; });
      var newSkills = skills.filter(function (s) { return existingIds.indexOf(s.id) === -1; });
      if (!newSkills.length) {
        this.notifyWarning('No new skills to add');
        return;
      }
      var self = this;
      newSkills.forEach(function (s) { self.labourer.skills.push(s); });
      await this.update();
      this.hideSkillModal();
      this.notifySuccess(newSkills.length + ' skill(s) added');
    },

    // ============ VENDORS ============
    showVendorModal() { this.showVendorForm = true; },
    hideVendorModal() { this.showVendorForm = false; },

    deleteVendorRow(item) {
      var idx = this.labourer.labourerVendors.findIndex(function (v) { return v.id === item.id; });
      if (idx >= 0) {
        this.labourer.labourerVendors.splice(idx, 1);
        this.update();
      }
    },

    // ============ PARENTS ============
    showParentModal() { this.showParentForm = true; },
    hideParentModal() { this.showParentForm = false; },

    deleteParentRow(item) {
      var idx = this.labourer.parents.findIndex(function (p) { return p.id === item.id; });
      if (idx >= 0) {
        this.labourer.parents.splice(idx, 1);
        this.update();
      }
    },

    async addParents(parent) {
      this.labourer.parents.push(parent);
      await this.update();
      this.hideParentModal();
      this.notifySuccess('Relative added');
    },

    // ============ EDUCATION ============
    showEducationModal() { this.showEducationForm = true; },
    hideEducationModal() { this.showEducationForm = false; },

    deleteEducationRow(item) {
      var idx = this.labourer.educations.findIndex(function (e) { return e.id === item.id; });
      if (idx >= 0) {
        this.labourer.educations.splice(idx, 1);
        this.update();
      }
    },

    async addEducations(education) {
      this.labourer.educations.push(education);
      await this.update();
      this.hideEducationModal();
      this.notifySuccess('Education added');
    },

    // ============ ACTIVITY WORKPLACES ============
    showActivityWorkplaceModal() { this.showActivityWorkplaceForm = true; },
    hideActivityWorkplaceModal() { this.showActivityWorkplaceForm = false; },

    async addActivityWorkplace(workplace) {
      try {
        this.labourer.activityWorkplaces.push(workplace);
        await this.get();
      } catch (e) {
        this.notifyException(e);
      }
    },

    async endActivityWorkplace(item) {
      var now = dayjs();
      var currentDateTimeStr = now.format('YYYY-MM-DDTHH:mm');

      var result = await Swal.fire({
        title: 'Set End Date & Time',
        text: 'Please enter the final processing end date and time for this assignment',
        input: 'text',
        inputValue: currentDateTimeStr,
        showCancelButton: true,
        confirmButtonText: 'Save Assignment',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'md-button md-success',
          cancelButton: 'md-button md-danger'
        },
        buttonsStyling: false,
        inputAttributes: {
          type: 'datetime-local',
          min: item.start_date ? dayjs(item.start_date).format('YYYY-MM-DDTHH:mm') : '',
          max: currentDateTimeStr
        },
        inputValidator: function (value) {
          if (!value) return 'End date and time is required';
          var selectedDate = dayjs(value);
          var startDate = dayjs(item.start_date);
          if (selectedDate.isBefore(startDate)) return 'End time cannot be earlier than start time';
          if (selectedDate.isAfter(now)) return 'End time cannot be in the future';
        }
      });

      if (!result.value) return;

      try {
        var activityWorkplace = this.labourer.activityWorkplaces.find(function (a) {
          return a.id === item.id;
        });
        if (!activityWorkplace) {
          this.notifyError('Assignment not found');
          return;
        }

        var formattedEnd = result.value ? result.value.replace('T', ' ') + ':00' : null;
        var formattedStart = this.toLocal(activityWorkplace.start_date, 'YYYY-MM-DD HH:mm:ss');

        await this.$store.dispatch('activityLabourerWorkplaces/update', Object.assign({}, activityWorkplace, {
          start_date: formattedStart,
          end_date: formattedEnd
        }));

        this.notifySuccess('Assignment end date updated');
        await this.get();
      } catch (error) {
        this.notifyException(error);
      }
    },

    async deletePermanentActivityWorkplace(item) {
      if (!this.can('delete-labourer-activity-workplace')) {
        return this.notifyError('Not authorized');
      }

      var confirm = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently remove this assignment record!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!confirm.value) return;

      try {
        await this.$store.dispatch('activityLabourerWorkplaces/destroy', item.id);
        this.labourer.activityWorkplaces = this.labourer.activityWorkplaces.filter(function (a) {
          return a.id !== item.id;
        });
        await Swal.fire('Deleted!', 'Assignment removed.', 'success');
      } catch (e) {
        this.notifyException(e);
      }
    },

    // ============ STATUSES ============
    showStatusModal() { this.showStatusForm = true; },
    hideStatusModal() { this.showStatusForm = false; },

    async addStatus() {
      await this.update();
      this.hideStatusModal();
      this.notifySuccess('Status updated');
    },

    // ============ TAKEOON ============
    viewTakeon(takeon) {
      this.$router.push({ name: 'View Labourer Schedule', params: { id: takeon.id } });
    },

    // ============ BIOTIME ============
    async biotimeRegistration(force) {
      try {
        await this.$store.dispatch('labourers/registrationBiotime', {
          id: this.labourer.id,
          force_creation: force
        });
        this.notifySuccess('Labourer registered to biometric system');
        await this.get();
      } catch (error) {
        this.notifyError('Failed to register Biotime');
      }
    },

    async goToBiotimeSync() {
      if (!this.labourer.biotime_id) {
        this.notifyWarning('Labourer not registered to biometric system');
        return;
      }

      var confirmation = await Swal.fire({
        title: 'Retrieve Punch Data?',
        text: 'This will retrieve the last 100 punch records from the biometric system.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, retrieve',
        cancelButtonText: 'Cancel'
      });

      if (confirmation.value) {
        try {
          await this.$store.dispatch('labourers/syncPunch', this.labourer);
          this.notifySuccess('Punch retrieval request submitted');
        } catch (error) {
          this.notifyException(error);
        }
      }
    },

    // ============ ATTACHMENTS ============
    async getAttachments() {
      try {
        await this.$store.dispatch('attachments/list', {
          filter: { attachment_id: this.labourer.id, attachment_type: 'Labourer' }
        });
        this.attachmentsLists = this.$store.getters['attachments/list'] || [];
      } catch (error) {
        console.error('Error fetching attachments:', error);
      }
    },

    triggerFileUpload() { this.$refs.fileInput.click(); },

    onFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.file = files[0];
      this.fileName = this.file.name;
    },

    isImage(path) {
      if (!path) return false;
      var exts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      return exts.some(function (ext) { return path.toLowerCase().indexOf(ext) !== -1; });
    },

    getFileName(path) {
      if (!path) return 'Unknown';
      var parts = path.split('/');
      return parts[parts.length - 1];
    },

    previewAttachment(attachment) {
      this.previewDialog.url = this.default_path + attachment.path;
      this.previewDialog.isImage = this.isImage(attachment.path);
      this.previewDialog.active = true;
    },

    async addAttachment() {
      if (!this.file) return this.notifyWarning('Please select a file');
      if (!this.attachment.attachment_type_id) return this.notifyWarning('Please select an attachment type');

      this.isSubmitting = true;
      this.attachment.attachment_id = this.labourer.id;

      try {
        await this.$store.dispatch('attachments/upload', {
          item: this.labourer,
          image: this.file
        });
        this.attachment.path = this.$store.getters['attachments/url'];
        await this.$store.dispatch('attachments/add', this.attachment);

        this.notifySuccess('Attachment added');
        this.attachment.attachment_type_id = null;
        this.file = null;
        this.fileName = null;
        this.$refs.fileInput.value = '';
        await this.getAttachments();
      } catch (error) {
        this.notifyException(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async destroyAttachment(id) {
      var confirmation = await Swal.fire({
        title: 'Delete Attachment?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      });
      if (confirmation.value) {
        try {
          await this.$store.dispatch('attachments/destroy', id);
          this.notifySuccess('Attachment deleted');
          await this.getAttachments();
        } catch (error) {
          this.notifyException(error);
        }
      }
    },

    async getAttachmentTypes() {
      try {
        await this.$store.dispatch('attachmentTypes/list');
        this.typesOptions = this.$store.getters['attachmentTypes/list'] || [];
      } catch (error) {
        console.error('Error fetching attachment types:', error);
      }
    },

    async getStatusOptions() {
      try {
        await this.$store.dispatch('statusOptions/list', {
          filter: { model: 'Labourer', is_active: 1 }
        });
        this.statusOptions = this.$store.getters['statusOptions/list'] || [];
      } catch (error) {
        console.error('Error fetching status options:', error);
      }
    },

    // ============ ISSUES ============
    fetchLabourerIssues() {
      var self = this;
      this.$store.dispatch('labourerIssues/fetchIssues', {
        filter: { labourer_id: this.labourer.id }
      }).then(function (response) {
        self.localIssues = response.data || [];
        self.localStats = response.stats;
      }).catch(function (error) {
        console.error('Error fetching issues:', error);
      });
    },

    resolveIssue(issue) {
      this.resolveIssueId = issue.id;
      this.resolveComments = '';
      this.showResolveModal = true;
    },

    editIssue(issue) {
      this.selectedIssue = issue;
      this.showIssueModal = true;
    },

    onIssueSuccess() {
      this.fetchLabourerIssues();
    },

    confirmResolve() {
      var self = this;
      this.isResolving = true;

      this.$store.dispatch('labourerIssues/resolveIssue', {
        labourerId: this.labourerId,
        issueId: this.resolveIssueId,
        comments: this.resolveComments
      }).then(function () {
        self.$store.dispatch('alerts/success', 'Issue resolved');
        self.showResolveModal = false;
        self.fetchLabourerIssues();
      }).catch(function (error) {
        self.$store.dispatch('alerts/error', 'Failed to resolve issue');
      }).finally(function () {
        self.isResolving = false;
      });
    },

    // ============ NAVIGATION ============
    goToEdit(id) {
      this.$router.push({ name: 'Edit Labourer', params: { id: id } });
    },

    goBack() {
      this.$router.push({ name: 'Labourers List' });
    }
  }
};
</script>

<style lang="scss" scoped>
.labourer-profile {
  margin-bottom: 24px;
}

.tab-content {
  padding: 0;
}

/* ============ BIOTIME AREA CODE CHIP ============ */
.area-code-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #f0f0f5;
  color: #555;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
}

/* ============ ATTACHMENT ============ */
.attachment-form {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  border: 1px solid #e8e8e8;
}

.file-input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.file-name {
  font-size: 12px;
  color: #666;
}

.form-actions {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
}

.attachment-preview {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
}

.file-icon {
  font-size: 32px;
  color: #666;
}

.attachment-link {
  color: #667eea;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.preview-container {
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 500px;
}

.preview-pdf {
  width: 100%;
  height: 500px;
  border: none;
}

/* ============ EMPTY STATE ============ */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;

  .md-icon {
    font-size: 48px !important;
    width: 48px !important;
    height: 48px !important;
    color: #ddd;
    margin-bottom: 12px;
  }

  p {
    margin: 0;
  }
}

/* ============ ISSUES ============ */
.issues-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.issue-item {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 18px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
}

.issue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.issue-category {
  display: flex;
  align-items: center;
  gap: 8px;
}

.issue-description {
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.5;
}

.issue-meta {
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.issue-actions {
  display: flex;
  gap: 5px;
  margin-top: 10px;
}

.resolve-modal .md-dialog {
  max-width: 450px;
  width: 90%;
}

@media (max-width: 768px) {
  .attachment-form .md-layout {
    flex-direction: column;
    gap: 15px;
  }
}
</style>
